import * as Sentry from '@sentry/react';
import { getPath } from '../services/objectPath';

const CLIENT_ID_KEY = 'analytics_client_id';
const SESSION_START_PREFIX = 'analytics_session_start:';

let deviceId: string | undefined;

export const uuidv4 = (): string => {
  const c =
    typeof crypto !== 'undefined' && 'getRandomValues' in crypto
      ? crypto
      : undefined;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c) c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h
    .slice(6, 8)
    .join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
};

const djb2 = (input: string): string => {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

export const getClientId = (): string => {
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const created = uuidv4();
    localStorage.setItem(CLIENT_ID_KEY, created);
    return created;
  } catch {
    return uuidv4();
  }
};

export const getSessionId = (uuid: string | undefined): string | undefined =>
  uuid;

export const getSessionElapsedMs = (uuid: string): number => {
  const key = SESSION_START_PREFIX + uuid;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored) return Date.now() - Number(stored);
    sessionStorage.setItem(key, String(Date.now()));
    return 0;
  } catch {
    return 0;
  }
};

// Only stable components feed the id: a browser update or a changed setting must not turn one
// device into a new one.
export const setDeviceIdFromCollection = (
  collection: { stableKeys?: string[]; components?: Record<string, unknown> } | undefined,
): void => {
  try {
    if (!collection?.components || !collection.stableKeys?.length) {
      deviceId = undefined;
      return;
    }
    const { components, stableKeys } = collection;
    deviceId = djb2(JSON.stringify(stableKeys.map((key) => [key, getPath(components, key)])));
  } catch {
    deviceId = undefined;
  }
};

export const getDeviceId = (): string | undefined => deviceId;

export const applyUserIdentity = (): void => {
  try {
    Sentry.setUser({ id: getClientId() });
  } catch {
    // analytics must never break UX
  }
};
